import React, { useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { LayoutGrid, ShoppingBag, MailQuestion, Settings2, LogOut, ArrowLeft, Activity, Menu } from "lucide-react";
import BrandMark from "./BrandMark";
import { useAdminAuth } from "../context/AdminAuthContext";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";

const NAV = [
  { to: "/admin", label: "Overview", icon: LayoutGrid, end: true },
  { to: "/admin/activity", label: "Activity", icon: Activity },
  { to: "/admin/orders", label: "Orders", icon: ShoppingBag },
  { to: "/admin/consults", label: "Consults", icon: MailQuestion },
  { to: "/admin/content", label: "Content & Pricing", icon: Settings2 },
];

function AdminNav({ onNavigate }) {
  const { me, logout } = useAdminAuth();
  const navigate = useNavigate();
  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-center gap-3 px-5 pb-5 pt-6">
        <BrandMark size={32} />
        <div className="leading-tight">
          <div className="font-serif-display text-[16px] font-semibold">
            Admin<span className="italic text-brand"> Console</span>
          </div>
          <div className="font-mono text-[9px] uppercase tracking-[0.28em] text-brand-mute">
            AuthorOS · v1.0
          </div>
        </div>
      </div>
      <nav className="mt-4 flex-1 overflow-y-auto px-3">
        {NAV.map((n) => {
          const Icon = n.icon;
          return (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              onClick={onNavigate}
              className={({ isActive }) =>
                `mb-1 flex min-h-[44px] items-center gap-3 rounded-md px-3 py-2.5 text-[13px] transition-colors ${
                  isActive ? "bg-brand/10 text-brand" : "text-brand-ivory/80 hover:bg-white/[0.04]"
                }`
              }
            >
              <Icon className="h-4 w-4" />
              <span>{n.label}</span>
            </NavLink>
          );
        })}
      </nav>
      <div className="border-t border-brand-hair px-5 py-4">
        <button
          onClick={() => { onNavigate && onNavigate(); navigate("/dashboard"); }}
          className="mb-3 flex min-h-[36px] items-center gap-2 font-mono text-[10px] uppercase tracking-[0.28em] text-brand-mute hover:text-brand"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back to studio
        </button>
        <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-brand-mute">Signed in</div>
        <div className="mt-1 truncate text-[13px] text-brand-ivory">{me?.email || "admin"}</div>
        <button
          onClick={() => { onNavigate && onNavigate(); logout(); navigate("/admin/login"); }}
          className="mt-3 flex min-h-[36px] items-center gap-2 font-mono text-[10px] uppercase tracking-[0.28em] text-brand-mute hover:text-brand"
        >
          <LogOut className="h-3.5 w-3.5" /> Sign out
        </button>
      </div>
    </div>
  );
}

export default function AdminLayout({ children }) {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const current = NAV.find((n) => (n.end ? location.pathname === n.to : location.pathname.startsWith(n.to)));

  return (
    <div className="flex min-h-screen bg-brand-ink text-brand-ivory">
      {/* Desktop sidebar */}
      <aside className="sticky top-0 hidden h-screen w-[240px] flex-none flex-col self-start border-r border-brand-hair bg-brand-panel lg:flex">
        <AdminNav />
      </aside>

      <main className="flex-1 overflow-x-hidden">
        {/* Mobile top bar */}
        <div className="sticky top-0 z-30 flex items-center justify-between gap-3 border-b border-brand-hair bg-brand-panel px-5 py-3 lg:hidden">
          <div className="flex items-center gap-2 min-w-0">
            <BrandMark size={26} />
            <div className="min-w-0 leading-tight">
              <div className="font-serif-display text-[14px] font-semibold">
                Admin<span className="italic text-brand"> Console</span>
              </div>
              <div className="truncate font-mono text-[9px] uppercase tracking-[0.24em] text-brand-mute">
                {current?.label || "Studio"}
              </div>
            </div>
          </div>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                aria-label="Open admin menu"
                data-testid="admin-mobile-menu-btn"
                className="inline-flex h-11 w-11 items-center justify-center rounded-md border border-brand-hair text-brand-ivory transition-colors hover:border-brand hover:text-brand"
              >
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[280px] max-w-[85vw] border-r border-brand-hair bg-brand-panel p-0 text-brand-ivory">
              <AdminNav onNavigate={() => setOpen(false)} />
            </SheetContent>
          </Sheet>
        </div>

        <div className="mx-auto max-w-[1240px] px-5 py-8 sm:px-8 sm:py-10">{children}</div>
      </main>
    </div>
  );
}
